import type { CompareProduct } from '@/compare/types';

import { getCompareProducts, setCompareProducts } from '../compare/storage/compare-storage';
import { renderCompareOffcanvasContent } from '../compare/render/render-compare-offcanvas';
import { renderComparePanel as renderPanel } from '../compare/render/render-compare-bottom-panel';
import { extractProductData } from '../compare/utils/product-factory';
import { initAddToCartListener } from '../compare/utils/compare-cart';
import CustomModal from './custom-modal';

declare global {
  interface Window {
    compareTranslations?: {
      infoTooltip: string;
      addToCart: string;
      priceNew: string;
      productsQnt: string;
      save: string;
      from_price: string;
      open_compare: string;
    };
  }
}

const MAX_COMPARE = 3;
let isLimitErrorVisible = false;
let isMinimized = false;

function renderComparePanel(open: boolean = true) {
  renderPanel(isMinimized, isLimitErrorVisible, open);
}

function updateCompareToggles() {
  const products = getCompareProducts();
  document.querySelectorAll<HTMLInputElement>('.compare-toggle-wrapper input[type="checkbox"]').forEach((input) => {
    const id = input.value;
    const isActive = products.some((p) => p.id === id);
    input.checked = isActive;
    input.disabled = !isActive && products.length >= MAX_COMPARE;
  });
}

function onCompareChange(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.matches('.compare-toggle-wrapper input[type="checkbox"]')) return;

  let products = getCompareProducts();

  if (target.checked) {
    if (products.length >= MAX_COMPARE) {
      target.checked = false;
      isLimitErrorVisible = true;
      renderComparePanel();
      return;
    }

    const product = extractProductData(target);

    if (!products.find((p) => p.id === product.id)) {
      products.push(product);
      setCompareProducts(products);
      isMinimized = false;
    }
  } else {
    const productId = target.value;
    products = products.filter((p) => p.id !== productId);
    setCompareProducts(products);
    isLimitErrorVisible = false;
  }

  updateCompareToggles();
  renderComparePanel();

  if (e.type === 'change') {
    e.preventDefault();
  }
}

document.addEventListener('change', onCompareChange);

function handleCompareToggle() {
  // Handle click on wrapper for disabled inputs
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const wrapper = target.closest('.compare-toggle-wrapper');
    if (wrapper) {
      const input = wrapper.querySelector('input') as HTMLInputElement;
      if (input && input.disabled && getCompareProducts().length >= MAX_COMPARE) {
        isLimitErrorVisible = true;
        isMinimized = false;
        renderComparePanel();
      }
    }
  });
}

async function fetchProductsByIds(ids: string[]) {
  const numericIds = ids
    .map((id) => {
      const match = id.match(/\d+$/);
      return match ? match[0] : id;
    })
    .filter(Boolean);

  if (numericIds.length === 0) return null;

  try {
    const response = await fetch(`${window.location.origin}/products.json?ids=${numericIds.join(',')}`);
    if (!response.ok) return null;

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : null;
  } catch {
    return null;
  }
}

// --- OFFCANVAS LOGIC ---
export function openCompareOffcanvas() {
  const offcanvas = document.getElementById('compare-offcanvas');
  const backdrop = document.getElementById('compare-backdrop');
  const panelFull = document.getElementById('compare-panel-full');

  if (offcanvas) offcanvas.classList.remove('translate-x-full');
  if (backdrop) backdrop.classList.remove('hidden');
  if (panelFull) panelFull.classList.add('hidden');
  renderCompareOffcanvasContent();
}

export function closeCompareOffcanvas() {
  const offcanvas = document.getElementById('compare-offcanvas');
  const backdrop = document.getElementById('compare-backdrop');
  const panelFull = document.getElementById('compare-panel-full');
  if (offcanvas) offcanvas.classList.add('translate-x-full');
  if (backdrop) backdrop.classList.add('hidden');
  if (panelFull && !isMinimized) panelFull.classList.remove('hidden');
}

async function restoreCompareFromUrl() {
  const savedBuffer = sessionStorage.getItem('compare_sync_buffer');
  const isSharedCompare = window.location.search.includes('id=');
  let compareIds: string[] = [];

  if (savedBuffer) {
    compareIds = JSON.parse(savedBuffer);
    sessionStorage.removeItem('compare_sync_buffer');
  } else {
    const url = new URL(window.location.href);
    const compareParam = url.searchParams.get('compare');

    compareIds = compareParam ? compareParam.split(',').filter(Boolean) : [];
  }

  if (compareIds.length === 0) return;

  let restoredProducts: CompareProduct[] = [];

  for (const id of compareIds) {
    const input = document.querySelector<HTMLInputElement>(`.compare-toggle-wrapper input[value="${id}"]`);
    if (input) {
      restoredProducts.push(extractProductData(input));
      input.checked = true;
    } else {
      const products = await fetchProductsByIds([id]);

      if (products?.length) {
        const product = products[0];

        restoredProducts.push({
          id,
          title: product.title,
          image: product.featured_image?.url || product.image,
          price: product.price,
          url: product.url,
          variants: [],
          tags: [],
          currentVariantId: '',
          optionNames: '',
          features: [],
        });
      }
    }
  }
  if (isSharedCompare) {
    sessionStorage.setItem('shared_compare_products', JSON.stringify(restoredProducts));
  } else {
    setCompareProducts(restoredProducts);
  }
  setTimeout(() => {
    const modal = document.querySelector('compare-drawer') as CustomModal;
    modal?.show();
  });
  openCompareOffcanvas();
  updateCompareToggles();
}

document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;

  // click on icon/button
  const trigger = target.closest('.save-info-trigger');

  // close all tooltips first
  document.querySelectorAll('.save-tooltip').forEach((tooltip) => {
    if (!trigger || !tooltip.closest('.save-info-trigger')?.contains(trigger)) {
      tooltip.classList.add('hidden');
    }
  });

  // if clicked trigger → toggle current tooltip
  if (trigger) {
    const tooltip = trigger.querySelector('.save-tooltip') as HTMLElement;

    if (tooltip) {
      tooltip.classList.toggle('hidden');
    }

    e.stopPropagation();
  }
});

function setupOffcanvasListeners() {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('.open-compare-btn')) {
      e.preventDefault();
      openCompareOffcanvas();
    }
    if (target.closest('#close-compare-offcanvas') || target.id === 'compare-backdrop') {
      closeCompareOffcanvas();
    }
  });
}

let lastScrollY = window.scrollY;
let lastScrollTime = performance.now();

function initMobileScrollMinimize() {
  window.addEventListener(
    'scroll',
    () => {
      if (window.innerWidth > 768) return;

      const currentScrollY = window.scrollY;
      const currentScrollTime = performance.now();

      const distance = currentScrollY - lastScrollY;
      const timeDiff = currentScrollTime - lastScrollTime;

      if (distance <= 0 || timeDiff <= 0) {
        lastScrollY = currentScrollY;
        lastScrollTime = currentScrollTime;
        return;
      }

      const speed = distance / timeDiff;

      if (speed > 2.5 && !isMinimized && getCompareProducts().length > 0) {
        isMinimized = true;
        renderComparePanel();
      }

      lastScrollY = currentScrollY;
      lastScrollTime = currentScrollTime;
    },
    { passive: true },
  );
}

function init(open: boolean = true) {
  const panel = document.getElementById('compare-panel');
  if (panel) {
    // Expand/Minimize Listeners
    document.getElementById('compare-minimize')?.addEventListener('click', () => {
      isMinimized = true;
      renderComparePanel();
    });
    document.getElementById('compare-expand')?.addEventListener('click', () => {
      isMinimized = false;
      renderComparePanel();
    });
    // Clear All Listener
    document.getElementById('compare-clear')?.addEventListener('click', () => {
      setCompareProducts([]);
      isLimitErrorVisible = false;
      renderComparePanel();
      updateCompareToggles();
    });
    // Close Error Listener
    document.getElementById('compare-error-close')?.addEventListener('click', () => {
      isLimitErrorVisible = false;
      renderComparePanel();
    });

    renderComparePanel(open);
    updateCompareToggles();
    initAddToCartListener();
    initMobileScrollMinimize();
  }
}

setupOffcanvasListeners();
handleCompareToggle();

// Initializers
restoreCompareFromUrl().then(() => {
  const url = new URL(window.location.href);
  const compareParam = url.searchParams.get('compare');

  const compareIds = compareParam ? compareParam.split(',').filter(Boolean) : [];
  const isSharedCompare = sessionStorage.getItem('shared_compare_products') !== null;

  const hasIds = compareIds.length > 0;
  if (hasIds) {
    init(false);
  } else {
    init();
  }
  if (!hasIds || !isSharedCompare) return;

  const open = () => {
    renderCompareOffcanvasContent();
    document.querySelector<HTMLButtonElement>('.open-compare-btn')?.click();
  };

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    requestAnimationFrame(open);
  } else {
    window.addEventListener('load', () => requestAnimationFrame(open), { once: true });
  }
});

document.addEventListener('compare:updated', () => {
  if (getCompareProducts().length < MAX_COMPARE) {
    isLimitErrorVisible = false;
  }

  renderComparePanel();
  updateCompareToggles();
});

document.addEventListener('shopify:section:load', init);
